/**
 * Synthesizer Architecture
 *
 * This file provides an interface-based architecture for interchangeable synthesizers.
 *
 * Available implementations:
 * - PianoSynth: Lightweight Web Audio API synth (no dependencies)
 * - ToneJsSynth: Advanced Tone.js-based synth with better sound quality
 * - PianoSamplerSynth: Real piano samples using Tone.Sampler for authentic sound
 * - SalamanderPianoSynth: Multi-velocity Salamander Grand Piano from CDN
 *
 * Usage:
 * ```typescript
 * const synth: Synth = new PianoSynth();  // or new ToneJsSynth() or new PianoSamplerSynth()
 * synth.resume();
 * synth.noteOn(60, 100);  // Play middle C
 * synth.noteOff(60);      // Stop middle C
 * ```
 */

import { Instrument, InstrumentOptions } from "tone/build/esm/instrument/Instrument";
import { Note } from "../music/note";
import { Sampler } from "tone";
import { isSampleBufferCached, loadSampleBuffers } from "./sample-buffer-cache";
import type { AudioProfile } from "@/lib/perf/perf-profile";
import { bytesForAudioBuffer, wouldExceedBudget } from "@/lib/perf/audio-budget-tracker";
import { getPerfProfileStore } from "@/lib/perf/detect-perf-tier";
import type { PerfProfileStore } from "@/lib/perf/perf-profile-store";
import { AudioFxChain } from "./audio-fx-chain";
import { samplerLayersHeld } from "./sampler-layer-hold";
import { ensureToneContext } from "./tone-context";

/** Velocity normalized between 0 and 1 */
export type Velocity = number; // 0-1

/**
 * Interface for interchangeable synthesizers
 */
export interface Synth {
  /**
   * Resume the audio context (needed after user gesture)
   */
  resume(): void;

  /**
   * Resolves once the synth has loaded enough samples to play notes
   * audibly. Sample-based synths use this to gate playback start , 
   * without it, the first notes scheduled before the samples land
   * are silently dropped (cf. `noteOn` early returns in
   * `SalamanderPianoSynth` / `PianoSamplerSynth`).
   *
   * Synths that don't load anything (pure Web Audio oscillators) return
   * `Promise.resolve()`, awaiting is then a no-op.
   */
  whenReady(): Promise<void>;

  /**
   * Start playing a note
   * @param note - MIDI note number (0-127)
   * @param velocity - Note velocity (0-1)
   */
  noteOn(note: number, velocity: Velocity, at?: number): void;

  /**
   * Stop playing a note
   * @param note - MIDI note number (0-127)
   */
  noteOff(note: number, at?: number): void;

  /**
   * Release all currently playing notes
   */
  releaseAllNotes(): void;

  /**
   * Dispose of the synth and free all audio resources
   */
  dispose(): void;
}

interface Voice {
  oscillators: OscillatorNode[];
  gain: GainNode;
}

function midiNoteToHz(note: number): number {
  return 440 * Math.pow(2, (note - 69) / 12);
}

export class PianoSynth implements Synth {
  private master: GainNode;
  private voices = new Map<number, Voice>();

  constructor(private readonly ctx: AudioContext) {
    this.master = this.ctx.createGain();
    this.master.gain.value = 0.6;
    this.master.connect(this.ctx.destination);
  }

  // AudioContext must be resumed after a user gesture.
  // `!== "running"` et non `=== "suspended"` : Safari pose aussi
  // `interrupted`, qui ne figure pas dans le type du DOM et qui est
  // précisément l'état d'où il faut sortir.
  resume() {
    if (this.ctx.state !== "running") {
      void this.ctx.resume().catch(() => undefined);
    }
  }

  // No samples to load, ready immediately.
  whenReady(): Promise<void> {
    return Promise.resolve();
  }

  noteOn(note: number, velocity: Velocity, at?: number) {
    // Stop any already-playing voice for this note
    this.noteOff(note, at);

    const freq = midiNoteToHz(note);
    const now = at ?? this.ctx.currentTime;
    const vol = velocity * 0.5;

    const gain = this.ctx.createGain();
    gain.connect(this.master);

    // ADSR: piano-like percussive envelope
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(vol, now + 0.005);   // attack 5 ms
    gain.gain.exponentialRampToValueAtTime(vol * 0.3, now + 0.4); // decay
    gain.gain.setValueAtTime(vol * 0.3, now + 0.4);        // sustain

    const oscillators: OscillatorNode[] = [];

    // Fundamental (sine)
    const osc1 = this.ctx.createOscillator();
    osc1.type = "sine";
    osc1.frequency.value = freq;
    osc1.connect(gain);
    osc1.start(now);
    oscillators.push(osc1);

    // 2nd harmonic (triangle, lower volume), adds warmth
    const osc2 = this.ctx.createOscillator();
    osc2.type = "triangle";
    osc2.frequency.value = freq * 2;
    const harmGain = this.ctx.createGain();
    harmGain.gain.value = 0.15;
    osc2.connect(harmGain);
    harmGain.connect(gain);
    osc2.start(now);
    oscillators.push(osc2);

    this.voices.set(note, { oscillators, gain });
  }

  noteOff(note: number, at?: number) {
    const voice = this.voices.get(note);
    if (!voice) {
return;
}

    const now = Math.max(at ?? this.ctx.currentTime, this.ctx.currentTime);
    const { gain, oscillators } = voice;

    // Release smooth (anti-pop)
    gain.gain.cancelScheduledValues(now);
    gain.gain.setTargetAtTime(0, now, 0.03); // très smooth
    const stopAt = now + 0.2;
    oscillators.forEach(osc => osc.stop(stopAt));

    // gain.gain.cancelScheduledValues(now);
    // // important: repartir d'une valeur "connue" évite le jump.
    // // On force une valeur minuscule si jamais c'est déjà à 0.
    // const safe = Math.max(gain.gain.value, 0.0001);
    // gain.gain.setValueAtTime(safe, now);
    // gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.25);
    //
    // const stopAt = now + 0.3;
    // oscillators.forEach((osc) => osc.stop(stopAt));

    this.voices.delete(note);

    // nettoyage après stop
    setTimeout(() => {
      try {
 gain.disconnect(); 
} catch {}

      oscillators.forEach(osc => {
        try {
 osc.disconnect(); 
} catch {}
      });
    }, (stopAt - this.ctx.currentTime) * 1000 + 20);
  }

  releaseAllNotes(): void {
    for (const [note] of this.voices) {
      this.noteOff(note);
    }

    this.voices.clear();
  }

  dispose(): void {
    this.releaseAllNotes();
    try {
 this.master.disconnect(); 
} catch {}
  }
}

/**
 * Tone.js Synth implementation
 * Uses Tone.js library for rich synthesizer sounds
 *
 * This is a more advanced synth with better sound quality than PianoSynth
 */
export class ToneJsSynth implements Synth {
  private synth: Instrument<InstrumentOptions>|null = null;
  private reverb: any = null;
  private isReady = false;
  private activeNotes = new Map<number, string>();
  private Tone: any = null;
  private readyPromise: Promise<void>;

  constructor() {
    this.readyPromise = this.initSynth();
  }

  whenReady(): Promise<void> {
    return this.readyPromise;
  }

  private async initSynth() {
    // Only load on client side
    if (typeof window === "undefined") {
      return;
    }

    try {
      // Use dynamic import with destructuring
      const Tone = await import("tone");
      this.Tone = Tone;

      // Create a polyphonic synthesizer with piano-like settings
      this.synth = new Tone.PolySynth(Tone.Synth, {
        // oscillator: {
        //   type: "fatsawtooth",
        //   count: 3,
        //   spread: 30,
        // },
        // envelope: {
        //   attack: 0.01,
        //   decay: 0.1,
        //   sustain: 0.3,
        //   release: 1,
        // },
      }).toDestination();

      // Add some reverb for a richer sound
      this.reverb = new Tone.Reverb({
        decay: 1.5,
        preDelay: 0.01,
      }).toDestination();

      // await this.reverb.ready;

      this.synth.connect(this.reverb);
      this.isReady = true;

      console.log("ToneJsSynth initialized successfully");
    } catch (error) {
      console.error("Failed to load Tone.js:", error);
    }
  }

  resume(): void {
    if (this.Tone && typeof window !== "undefined") {
      this.Tone.start().catch((err: Error) => {
        console.error("Failed to resume Tone.js context:", err);
      });
    }
  }

  noteOn(note: number, velocity: Velocity, at?: number): void {
    if (!this.synth) {
return;
}
    // if (!this.isReady || !this.synth) {
    //   console.warn("ToneJsSynth not ready yet");
    //   return;
    // }

    try {
      const n = Note.fromMidi(note);

      const now = at ?? this.Tone.now();
      // Trigger the note with the velocity
      this.synth.triggerAttack(n.toName(), now, velocity);
      this.activeNotes.set(note, n.toName());
    } catch (error) {
      console.error("Error playing note:", error);
    }
  }

  noteOff(note: number, at?: number): void {
    if (!this.synth) {
return;
}

    try {
      const now = at ?? this.Tone.now();
      const noteName = this.activeNotes.get(note);
      if (noteName) {
        this.synth.triggerRelease(noteName, now);
        this.activeNotes.delete(note);
      }
    } catch (error) {
      console.error("Error stopping note:", error);
    }
  }

  releaseAllNotes(): void {
    if (!this.synth) {
return;
}

    try {
      for (const [note, noteName] of this.activeNotes) {
        this.synth.triggerRelease(noteName);
      }

      this.activeNotes.clear();
    } catch (error) {
      console.error("Error releasing all notes:", error);
    }
  }

  /**
   * Dispose of the synth and free resources
   */
  dispose(): void {
    this.releaseAllNotes();
    if (this.synth) {
      this.synth.dispose();
    }

    if (this.reverb) {
      this.reverb.dispose();
    }
  }
}

/**
 * Tone.js Sampler-based Piano Synth
 * Uses real piano samples for authentic piano sound
 *
 * This synth loads 84 piano samples (complete 7-octave range from A1 to C8)
 * and uses Tone.Sampler to play them with maximum fidelity.
 * No pitch-shifting required - each note has its own dedicated sample.
 */
export class PianoSamplerSynth implements Synth {
  private sampler: Sampler|null = null;
  private reverb: any = null;
  private dryGain: any = null;
  private wetGain: any = null;
  private isReady = false;
  private activeNotes = new Map<number, string>();
  private Tone: any = null;
  private cleanupInterval: any = null;
  // Resolved when the Tone.Sampler `onload` fires (or when init bails out
  //, we resolve in the catch path so callers don't hang forever).
  private resolveSamplesLoaded!: () => void;
  private samplesLoadedPromise: Promise<void> = new Promise<void>((resolve) => {
    this.resolveSamplesLoaded = resolve;
  });

  constructor(private ctx: AudioContext, private reverbMix = 0.15) {
    this.initSampler();
  }

  whenReady(): Promise<void> {
    return this.samplesLoadedPromise;
  }

  private async initSampler() {
    // Only load on client side
    if (typeof window === "undefined") {
      this.resolveSamplesLoaded();

      return;
    }

    try {
      // Use dynamic import
      const Tone = await import("tone");
      this.Tone = Tone;
      ensureToneContext(Tone, this.ctx);
      // await this.Tone.start();

      // Create a sampler with all piano samples (84 notes)
      // Using all samples for maximum fidelity - no pitch-shifting needed
      // Dry/wet bus: sampler → dryGain → destination
      //                     → reverb → wetGain → destination
      this.dryGain = new Tone.Gain(1 - this.reverbMix);
      this.wetGain = new Tone.Gain(this.reverbMix);
      this.dryGain.toDestination();
      this.wetGain.toDestination();

      this.reverb = new Tone.Reverb({
        decay: 1.5,
        preDelay: 0.01,
      });
      this.reverb.connect(this.wetGain);

      this.sampler = new Tone.Sampler({
        urls: {
          A1: "A1.mp3",
          "A#1": "As1.mp3",
          B1: "B1.mp3",
          C1: "C1.mp3",
          "C#1": "Cs1.mp3",
          D1: "D1.mp3",
          "D#1": "Ds1.mp3",
          E1: "E1.mp3",
          F1: "F1.mp3",
          "F#1": "Fs1.mp3",
          G1: "G1.mp3",
          "G#1": "Gs1.mp3",
          A2: "A2.mp3",
          "A#2": "As2.mp3",
          B2: "B2.mp3",
          C2: "C2.mp3",
          "C#2": "Cs2.mp3",
          D2: "D2.mp3",
          "D#2": "Ds2.mp3",
          E2: "E2.mp3",
          F2: "F2.mp3",
          "F#2": "Fs2.mp3",
          G2: "G2.mp3",
          "G#2": "Gs2.mp3",
          A3: "A3.mp3",
          "A#3": "As3.mp3",
          B3: "B3.mp3",
          C3: "C3.mp3",
          "C#3": "Cs3.mp3",
          D3: "D3.mp3",
          "D#3": "Ds3.mp3",
          E3: "E3.mp3",
          F3: "F3.mp3",
          "F#3": "Fs3.mp3",
          G3: "G3.mp3",
          "G#3": "Gs3.mp3",
          A4: "A4.mp3",
          "A#4": "As4.mp3",
          B4: "B4.mp3",
          C4: "C4.mp3",
          "C#4": "Cs4.mp3",
          D4: "D4.mp3",
          "D#4": "Ds4.mp3",
          E4: "E4.mp3",
          F4: "F4.mp3",
          "F#4": "Fs4.mp3",
          G4: "G4.mp3",
          "G#4": "Gs4.mp3",
          A5: "A5.mp3",
          "A#5": "As5.mp3",
          B5: "B5.mp3",
          C5: "C5.mp3",
          "C#5": "Cs5.mp3",
          D5: "D5.mp3",
          "D#5": "Ds5.mp3",
          E5: "E5.mp3",
          F5: "F5.mp3",
          "F#5": "Fs5.mp3",
          G5: "G5.mp3",
          "G#5": "Gs5.mp3",
          A6: "A6.mp3",
          "A#6": "As6.mp3",
          B6: "B6.mp3",
          C6: "C6.mp3",
          "C#6": "Cs6.mp3",
          D6: "D6.mp3",
          "D#6": "Ds6.mp3",
          E6: "E6.mp3",
          F6: "F6.mp3",
          "F#6": "Fs6.mp3",
          G6: "G6.mp3",
          "G#6": "Gs6.mp3",
          A7: "A7.mp3",
          "A#7": "As7.mp3",
          B7: "B7.mp3",
          C7: "C7.mp3",
          "C#7": "Cs7.mp3",
          D7: "D7.mp3",
          "D#7": "Ds7.mp3",
          E7: "E7.mp3",
          F7: "F7.mp3",
          "F#7": "Fs7.mp3",
          G7: "G7.mp3",
          "G#7": "Gs7.mp3",
        },
        baseUrl: "/samples/piano/",
        attack: 0.005,
        release: 1,
        onload: () => {
          console.log("Piano samples loaded successfully (84 notes)");
          this.isReady = true;
          this.resolveSamplesLoaded();
        },
      });

      this.sampler.connect(this.dryGain);
      this.sampler.connect(this.reverb);

      console.log("PianoSamplerSynth initialized successfully");
    } catch (error) {
      console.error("Failed to load Tone.js Sampler:", error);
      // Resolve anyway so awaiting `whenReady()` doesn't hang on init failure.
      this.resolveSamplesLoaded();
    }
  }

  resume(): void {
    if (this.Tone && typeof window !== "undefined") {
      this.Tone.start().catch((err: Error) => {
        console.error("Failed to resume Tone.js context:", err);
      });
    }
  }

  noteOn(note: number, velocity: Velocity, at?: number): void {
    if (!this.isReady || !this.sampler) {
      console.warn("PianoSamplerSynth not ready yet");

      return;
    }

    try {
      const n = Note.fromMidi(note);

      // Use immediate timing if no time specified
      const time = at !== undefined ? at : undefined;

      // Release any previous note with the same MIDI number first
      const previousNoteName = this.activeNotes.get(note);
      if (previousNoteName) {
        this.sampler.triggerRelease(previousNoteName);
      }

      // Apply a soft velocity curve for more natural piano feel
      const curved = Math.pow(velocity, 0.6);
      this.sampler.triggerAttack(n.toName(), time, curved);
      this.activeNotes.set(note, n.toName());
    } catch (error) {
      console.error("Error playing note:", error, note);
    }
  }

  noteOff(note: number, at?: number): void {
    if (!this.sampler) {
      return;
    }

    try {
      const noteName = this.activeNotes.get(note);
      if (noteName) {
        // Use immediate timing if no time specified
        const time = at !== undefined ? at : undefined;
        this.sampler.triggerRelease(noteName, time);
        this.activeNotes.delete(note);
      }
    } catch (error) {
      console.error("Error stopping note:", error, note);
    }
  }

  /**
   * Release all currently playing notes
   */
  releaseAllNotes(): void {
    if (!this.sampler) {
return;
}

    try {
      this.activeNotes.forEach((noteName) => {
        this.sampler!.triggerRelease(noteName);
      });
      this.activeNotes.clear();
    } catch (error) {
      console.error("Error releasing all notes:", error);
    }
  }

  /**
   * Dispose of the sampler and free resources
   */
  dispose(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
    }

    this.releaseAllNotes();
    if (this.sampler) {
      this.sampler.dispose();
    }

    if (this.reverb) {
      this.reverb.dispose();
    }

    if (this.dryGain) {
      this.dryGain.dispose();
    }

    if (this.wetGain) {
      this.wetGain.dispose();
    }
  }
}

// --- Salamander Grand Piano (multi-velocity, CDN-hosted) ---

/**
 * Notre propre origine, et surtout plus `tambien.github.io`.
 *
 * Le sampler lisait ses 480 fichiers sur GitHub Pages. Le studio étant monté
 * en permanence par la coque `(main)`, cela partait sur **toutes** les pages :
 * une visite de `/confidentialite` ouvrait 30 requêtes vers Microsoft, hors
 * Union européenne, en lui révélant l'adresse IP et le User-Agent du lecteur.
 * La politique de confidentialité nomme PostHog comme seul sous-traitant et
 * affirme qu'aucune donnée ne quitte l'Union : c'était faux tant que cette
 * constante pointait ailleurs.
 *
 * Accessoirement, le son du produit ne dépend plus de la page perso d'un tiers.
 *
 * Chemin relatif et non absolu : le front est servi sur plusieurs hôtes
 * (`studio.virtuosa.io`, les prévisualisations, `localhost`), et une URL
 * absolue en figerait un seul. Les fichiers sont posés dans `public/piano`,
 * que Next standalone sert depuis le disque.
 *
 * ⚠️ Ils ne sont **pas** dans git : `scripts/fetch-piano-samples.mjs` les
 * récupère, et le Dockerfile l'appelle dans un étage à part. En local, lancer
 * `npm run piano:samples` une fois, sans quoi le piano reste muet.
 */
const SALAMANDER_BASE_URL = "/piano/";

/**
 * Ce qu'une couche de vélocité pèse une fois décodée, tant qu'on n'en a pas
 * mesuré une : trente MP3 en float32, ≈ 150 Mo (cf. `AudioProfile`).
 */
const ESTIMATED_LAYER_BYTES = 150 * 1024 * 1024;

// Notes sampled every minor 3rd (30 notes, A0 to C8)
const SAMPLED_MIDI_NOTES = [
  21, 24, 27, 30, 33, 36, 39, 42, 45, 48, 51, 54,
  57, 60, 63, 66, 69, 72, 75, 78, 81, 84, 87, 90,
  93, 96, 99, 102, 105, 108,
];

function midiToSalamanderName(midi: number): string {
  const names = ["C", "Cs", "D", "Ds", "E", "F", "Fs", "G", "Gs", "A", "As", "B"];
  const octave = Math.floor(midi / 12) - 1;
  const note = names[midi % 12];

  return `${note}${octave}`;
}

function midiToToneName(midi: number): string {
  const names = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
  const octave = Math.floor(midi / 12) - 1;

  return `${names[midi % 12]}${octave}`;
}

/**
 * Salamander Grand Piano, lazy-loading multi-velocity sampler
 *
 * Loads one initial velocity layer (v8, middle) for instant playability,
 * then loads additional layers on demand as different velocities are requested.
 * Only downloads what is actually needed.
 */
export class SalamanderPianoSynth implements Synth {
  private layers: { sampler: Sampler; velocity: number }[] = [];
  private fxChain: AudioFxChain | null = null;
  private isReady = false;
  /**
   * Démonté. Une couche encore en route au moment du démontage — un partenaire
   * parti dans la seconde — se jette à l'arrivée au lieu de rester connectée
   * à rien.
   */
  private disposed = false;
  private loadedLayers = new Set<number>();
  private loadingLayers = new Set<number>();
  private activeNotes = new Map<number, { noteName: string; sampler: Sampler }>();
  /** Poids mesuré d'une couche décodée, dès qu'on en tient une. */
  private layerBytes: number | null = null;
  private Tone: any = null;
  private readonly perfStore: PerfProfileStore;
  /**
   * Resolves once the initial velocity layer (v8) is loaded and the synth
   * is safe to call `noteOn` on. Captured here so callers can `await`
   * before scheduling playback. Without it, notes scheduled before the
   * MP3 samples land are silently dropped (the early `if (!isReady)` in
   * `noteOn`), typical symptom: only the last 1-2 notes of a short
   * sequence are audible.
   */
  private readyPromise: Promise<void>;

  constructor(
    private ctx: AudioContext,
    // Part réverbérée. Voir `PIANO_TONE` dans `audio-fx-chain.ts` pour le
    // reste de la profondeur : au-delà de ~0,3 la note perd son attaque et
    // l'instrument s'entend depuis le fond de la salle, pas depuis le tabouret.
    private reverbMix = 0.16,
    private baseUrl = SALAMANDER_BASE_URL,
    perfStore?: PerfProfileStore,
  ) {
    // Hold the store, not a snapshot — `samplerStrategy` is read live in
    // `noteOn` so the perf coordinator can flip `lazy → single` mid-session
    // and prevent further layer loads on the fly.
    this.perfStore = perfStore ?? getPerfProfileStore();
    this.readyPromise = this.init();
  }

  private get audioProfile(): AudioProfile {
    return this.perfStore.value.audio;
  }

  whenReady(): Promise<void> {
    return this.readyPromise;
  }

  /**
   * Monte une couche de vélocité, sur des échantillons déjà décodés.
   *
   * Les buffers passent par `loadSampleBuffers` au lieu d'être confiés au
   * sampler sous forme d'URLs : le décodage devient un travail par fichier et
   * non par instance. C'est ce qui rend tenable un synthé par lane — sans quoi
   * la deuxième main d'un morceau, ou le troisième participant d'une séance,
   * repayait trente `decodeAudioData` sur le fil principal pour des
   * échantillons identiques.
   *
   * `Tone.Sampler` accepte des `ToneAudioBuffer` là où il accepte des chemins
   * (`SamplesMap`), et déclenche `onload` de façon différée quand tout est déjà
   * en mémoire — le rendez-vous par `onload` reste donc valable dans les deux
   * cas.
   */
  private async loadLayer(vLayer: number): Promise<void> {
    if (this.loadedLayers.has(vLayer) || this.loadingLayers.has(vLayer)) {
      return;
    }

    const files: Record<string, string> = {};

    for (const midi of SAMPLED_MIDI_NOTES) {
      files[midiToToneName(midi)] = `${midiToSalamanderName(midi)}v${vLayer}.mp3`;
    }

    // Une couche de plus ne se charge que si le budget mémoire du profil la
    // laisse passer ; sinon on s'en tient, sans bruit, aux couches déjà là —
    // `noteOn` prend la plus proche. La première couche, elle, passe toujours :
    // sans elle le piano est muet.
    if (this.loadedLayers.size > 0 && this.exceedsSampleBudget(files)) {
      return;
    }

    this.loadingLayers.add(vLayer);

    try {
      const buffers = await loadSampleBuffers(this.Tone, this.baseUrl, files);

      this.layerBytes = Object.values(buffers).reduce(
        (sum, buffer) => sum + bytesForAudioBuffer(buffer),
        0,
      );

      await new Promise<void>((resolve) => {
        const sampler = new this.Tone.Sampler({
          urls: buffers,
          attack: 0,
          release: 1.2,
          onload: () => {
            if (this.disposed) {
              sampler.dispose();
              this.loadingLayers.delete(vLayer);
              resolve();

              return;
            }

            this.fxChain?.connectSource(sampler);
            this.layers.push({ sampler, velocity: vLayer / 16 });
            this.loadedLayers.add(vLayer);
            this.loadingLayers.delete(vLayer);
            resolve();
          },
        });
      });
    } catch (error) {
      // La couche reste absente, et surtout plus « en cours » : une frappe
      // ultérieure sur la même vélocité pourra réessayer.
      this.loadingLayers.delete(vLayer);
      console.error(`SalamanderPianoSynth: layer v${vLayer} failed to load`, error);
    }
  }

  /**
   * Cette couche ferait-elle déborder `sampleBudgetMB` ?
   *
   * Seuls les fichiers absents du cache comptent : une couche déjà décodée
   * pour un autre synthé ne coûte rien de plus. Le poids d'un fichier est
   * celui mesuré sur la première couche, à défaut l'estimation.
   */
  private exceedsSampleBudget(files: Record<string, string>): boolean {
    const names = Object.values(files);
    let missing = 0;

    for (const name of names) {
      if (!isSampleBufferCached(this.baseUrl + name)) {
        missing += 1;
      }
    }

    if (missing === 0 || names.length === 0) {
      return false;
    }

    const perLayer = this.layerBytes ?? ESTIMATED_LAYER_BYTES;
    const extraMB = (perLayer * missing) / names.length / (1024 * 1024);

    return wouldExceedBudget(extraMB, this.audioProfile.sampleBudgetMB);
  }

  /** Find the ideal velocity layer (1-16) for a given velocity (0-1). */
  private velocityToLayer(v: number): number {
    return Math.max(1, Math.min(16, Math.round(v * 16)));
  }

  private async init() {
    if (typeof window === "undefined") {
return;
}

    try {
      const Tone = await import("tone");
      this.Tone = Tone;
      ensureToneContext(Tone, this.ctx);

      this.fxChain = new AudioFxChain(Tone, this.audioProfile, this.reverbMix);

      // Load initial layer (v8 = middle velocity) for instant playability
      await this.loadLayer(8);
      this.isReady = true;
    } catch (error) {
      console.error("Failed to init SalamanderPianoSynth:", error);
    }
  }

  resume(): void {
    if (this.Tone && typeof window !== "undefined") {
      this.Tone.start().catch((err: Error) => {
        console.error("Failed to resume Tone.js context:", err);
      });
    }
  }

  noteOn(note: number, velocity: Velocity, at?: number): void {
    if (!this.isReady || this.layers.length === 0) {
return;
}

    const n = Note.fromMidi(note);
    const noteName = n.toName();
    const time = at !== undefined ? at : undefined;

    // Release previous instance of this note with a fast cutoff
    // (simulates the damper briefly touching the string before restrike).
    // We temporarily override `release` on the sampler; use try/finally to
    // guarantee restoration even if `triggerRelease` throws.
    const prev = this.activeNotes.get(note);
    if (prev) {
      const origRelease = prev.sampler.release;
      try {
        prev.sampler.release = 0.08;
        prev.sampler.triggerRelease(prev.noteName, time);
      } finally {
        prev.sampler.release = origRelease;
      }
    }

    const v = Math.max(0.01, Math.min(1, velocity));

    // Find closest loaded layer
    let closest = this.layers[0];
    let minDist = Math.abs(v - closest.velocity);
    for (let i = 1; i < this.layers.length; i++) {
      const dist = Math.abs(v - this.layers[i].velocity);
      if (dist < minDist) {
        closest = this.layers[i];
        minDist = dist;
      }
    }

    closest.sampler.triggerAttack(noteName, time, v);
    this.activeNotes.set(note, { noteName, sampler: closest.sampler });

    // Pas de couche nouvelle pendant une séance : voir `sampler-layer-hold`.
    if (this.audioProfile.samplerStrategy === "lazy" && !samplerLayersHeld()) {
      const idealLayer = this.velocityToLayer(v);
      if (!this.loadedLayers.has(idealLayer) && !this.loadingLayers.has(idealLayer)) {
        this.loadLayer(idealLayer);
      }
    }
  }

  noteOff(note: number, at?: number): void {
    if (this.layers.length === 0) {
return;
}

    const active = this.activeNotes.get(note);
    if (!active) {
return;
}

    const time = at !== undefined ? at : undefined;
    active.sampler.triggerRelease(active.noteName, time);
    this.activeNotes.delete(note);
  }

  releaseAllNotes(): void {
    for (const [, { noteName, sampler }] of this.activeNotes) {
      try {
 sampler.triggerRelease(noteName); 
} catch {}
    }

    this.activeNotes.clear();
  }

  dispose(): void {
    this.disposed = true;
    this.releaseAllNotes();
    for (const layer of this.layers) {
      layer.sampler.dispose();
    }

    this.layers = [];
    this.fxChain?.dispose();
    this.fxChain = null;
  }
}
